import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

/**
 * BarcodeScanner Component
 *
 * Scans barcodes and QR codes using device camera (Web Camera API)
 * Supports: EAN-13, EAN-8, Code-128, UPC-A, QR Code
 *
 * @param {Function} onScan - Callback when scan is successful: (result) => void
 * @param {Function} onError - Callback when error occurs: (error) => void
 * @param {Boolean} isActive - Control scanner state (true = start, false = stop)
 * @param {Array} formats - Barcode formats to scan (default: all common formats)
 */

// Module-level so the identity is stable. An array literal built inside the
// component would be a new value every render and restart the camera.
const DEFAULT_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.QR_CODE
];

export default function BarcodeScanner({
  onScan,
  onError,
  isActive = false,
  formats = []
}) {
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  const formatsRef = useRef(formats);
  const [cameraError, setCameraError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Keep the latest callbacks reachable without making them effect
  // dependencies — callers pass inline arrows, which change every render.
  useEffect(() => {
    onScanRef.current = onScan;
    onErrorRef.current = onError;
    formatsRef.current = formats;
  });

  useEffect(() => {
    if (!isActive) return undefined;

    const readerId = 'barcode-reader';
    let cancelled = false;

    const start = async () => {
      let instance = null;
      try {
        instance = new Html5Qrcode(readerId);

        const requested = formatsRef.current;
        const config = {
          fps: 10, // Frames per second for scanning
          qrbox: { width: 300, height: 150 }, // Scanning box size
          formatsToSupport: requested.length > 0 ? requested : DEFAULT_FORMATS
        };

        await instance.start(
          { facingMode: 'environment' }, // Use back camera on mobile
          config,
          (decodedText, decodedResult) => {
            console.log('[BarcodeScanner] Scan success:', decodedText);
            onScanRef.current?.(decodedText, decodedResult);
          },
          () => {
            // Fires continuously while no barcode is in frame — ignore
          }
        );

        // The component may have unmounted while start() was in flight
        if (cancelled) {
          await instance.stop().catch(() => {});
          await instance.clear().catch(() => {});
          return;
        }

        scannerRef.current = instance;
        setIsScanning(true);
        setCameraError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('[BarcodeScanner] Failed to start:', err);
        setCameraError(err.message || 'ไม่สามารถเปิดกล้องได้');
        setIsScanning(false);
        onErrorRef.current?.(err);
      }
    };

    start();

    return () => {
      cancelled = true;
      const active = scannerRef.current;
      scannerRef.current = null;
      setIsScanning(false);

      if (active) {
        active.stop()
          .then(() => active.clear())
          .then(() => console.log('[BarcodeScanner] Stopped'))
          .catch(err => console.error('[BarcodeScanner] Failed to stop:', err));
      }
    };
  }, [isActive]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Scanner viewport */}
      <div
        id="barcode-reader"
        style={{
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          border: isScanning ? '2px solid #667eea' : '2px solid #444'
        }}
      />

      {/* Status messages */}
      {!isActive && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#888',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          📷 พร้อมสแกนบาร์โค้ด
        </div>
      )}

      {cameraError && (
        <div style={{
          textAlign: 'center',
          padding: '1rem',
          color: '#e74c3c',
          background: 'rgba(231, 76, 60, 0.1)',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          <p>❌ {cameraError}</p>
          <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#888' }}>
            โปรดอนุญาตการเข้าถึงกล้องในเบราว์เซอร์ หรือใช้ข้อมูลด้วยตนเอง
          </p>
        </div>
      )}

      {isScanning && (
        <div style={{
          textAlign: 'center',
          padding: '0.5rem',
          color: '#27ae60',
          fontSize: '0.9rem',
          marginTop: '0.5rem'
        }}>
          ✅ กำลังสแกน... โปรดนำบาร์โค้ดเข้าใกล้กล้อง
        </div>
      )}
    </div>
  );
}
